<script setup lang="ts">
import './assets/style.scss'
import { computed, type PropType, ref, useAttrs, watch } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { FileFieldTypes } from '@/widgets/FileLoadField/model/types.ts'
import { useConfirm, VIcons } from '@/shared/ui'
import { useHelpers, useTranslate } from '@/shared/lib'
import { useDownloadFieldFile, useRemoveFieldFiles, useUploadFieldFiles } from './../model/api.ts'
import { useMandatoryFieldError } from '@/entities/Inventory/TypeField/model/useMandatoryFieldError'

const props = defineProps({
  type: {
    type: String,
    default: '',
  },
  field: {
    type: Object as PropType<ObjectInventoryField>,
    required: true,
  },
  showMandatoryError: {
    type: Boolean,
    default: false,
  },
})

const value = defineModel<string | null>({ required: true })

const attrs = useAttrs()
const isFieldReadonly = computed(() => Boolean(attrs.readonly))

const {
  visible: mandatoryErrorVisible,
  message: mandatoryErrorMessage,
  markTouched,
  registerUserInteraction,
} = useMandatoryFieldError(
  () => ({
    showMandatoryError: props.showMandatoryError,
    mandatory: props.field?.mandatory,
    type: 'link_file',
  }),
  () => value.value,
)

function onBlur() {
  markTouched()
}

const { show } = useConfirm()
const { trs } = useTranslate()
const { formatFileSize, saveFile } = useHelpers()

const { upload, isPending } = useUploadFieldFiles()
const { remove, isPending: isRemovePending } = useRemoveFieldFiles()

const fileInput = ref<HTMLInputElement>()

/* Вычисляем допустимые типы файлов */
const accept = computed(() => {
  return FileFieldTypes[props.field?.file_type || 'DOCUMENT'].join(', ')
})

/**
 * Функция изменения файла
 */
const fileChangeHandler = () => {
  if (!fileInput.value?.files) return

  const data = {
    files: new FormData(),
  }

  for (const item of fileInput.value.files) {
    const splitName: string[] = item.name.split('.')
    const ext: string = splitName.pop() || ''

    data.files.append('files[]', item)
    data.files.append('file_names[]', splitName.join('.'))
    data.files.append('file_ext[]', ext)
    data.files.append('file_sizes[]', formatFileSize(item.size))
  }

  upload(data).then((result) => {
    if (result?.data?.files) {
      value.value = result.data.files[0].file
    }
  })
}

/**
 * Функция вызова меню выбора файла
 */
const focusFieldHandler = () => {
  registerUserInteraction()
  if (isFieldReadonly.value) return
  if (fileInput.value) {
    fileInput.value.click()
  }
}

/**
 * Функция выбора удаления файла
 */
const removeFileHandler = () => {
  if (isFieldReadonly.value) return
  show({
    title: trs('itf.remove_file'),
    confirmFunc: () =>
      remove({
        file: value.value,
      }).then(() => {
        value.value = ''
      }),
  })
}

const { refetch, data } = useDownloadFieldFile(value)

watch(data, () => {
  if (!data.value || !value.value) return

  const fileData = new Blob([data.value.data])
  saveFile(URL.createObjectURL(fileData), value.value)
})
</script>

<template>
  <div class="file-load-field__wrapper">
    <VTextField
      v-bind="$attrs"
      v-model="value"
      readonly
      :loading="isPending || isRemovePending"
      :error="mandatoryErrorVisible ? true : undefined"
      :error-messages="mandatoryErrorVisible ? mandatoryErrorMessage : undefined"
      @click="focusFieldHandler"
      @blur="onBlur"
    >
      <template v-slot:prepend-inner>
        <VIcons class="file-load-field__icons" type="attach_file_add" />
      </template>

      <template v-if="value && !isFieldReadonly" v-slot:append-inner>
        <VIcons
          class="file-load-field__icons"
          type="close"
          @click.stop.prevent="removeFileHandler"
        />
      </template>
    </VTextField>

    <VBtn color="warning" :disabled="!value" @click="refetch">
      <VIcons type="download" />
    </VBtn>

    <input
      class="file-load-field__input"
      :accept
      ref="fileInput"
      type="file"
      :disabled="isFieldReadonly"
      @change="fileChangeHandler"
    />
  </div>
</template>
